import { useMemo, useState } from "react";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSGroup, type SSOption } from "../ui/SearchSelect";
import { MCard, Sheet, MSelect } from "../mobile/primitives";

type Kind = "stock" | "semi" | "finished";
const KIND_LABELS: [Kind, string][] = [["stock", "Stok"], ["semi", "Yarı Mamül"], ["finished", "Mamul"]];

type MovementType =
  | "receipt" | "consumption" | "transfer" | "production" | "scrap"
  | "adjustment" | "shipment" | "reservation" | "return" | "count_diff";

const TYPE_META: Record<MovementType, { label: string; color: string }> = {
  receipt: { label: "Mal Kabul", color: "var(--success-text)" },
  consumption: { label: "Tüketim", color: "var(--danger)" },
  transfer: { label: "Transfer", color: "var(--info)" },
  production: { label: "Üretim", color: "var(--success-text)" },
  scrap: { label: "Fire", color: "var(--danger)" },
  adjustment: { label: "Düzeltme", color: "var(--warning-text)" },
  shipment: { label: "Sevkiyat", color: "var(--neutral-700)" },
  reservation: { label: "Rezervasyon", color: "var(--info)" },
  return: { label: "İade", color: "var(--success-text)" },
  count_diff: { label: "Sayım Farkı", color: "var(--warning-text)" },
};
const TYPE_OPTS = Object.entries(TYPE_META) as [MovementType, { label: string; color: string }][];

const REF_LABELS: Record<string, string> = {
  BATCH: "Parti", BATCH_REVERSAL: "Parti iptali", WO: "İş Emri", SO: "Sipariş",
  Receipt: "Mal Kabul", Transfer: "Transfer", QC: "Kalite", Count: "Sayım",
};

const fLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--neutral-500)", textTransform: "uppercase", letterSpacing: "0.04em" };

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3, signDisplay: "exceptZero" }).format(n);
}
function fmtQtyAbs(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(Math.abs(n));
}
function fmtDayLong(day: string): string {
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "long", year: "numeric", weekday: "long" }).format(new Date(`${day}T12:00:00`));
}
function fmtTime(ts: string): string {
  return new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit" }).format(new Date(ts));
}

interface Filters {
  dateFrom: string;
  dateTo: string;
  itemKind?: Kind;
  itemId?: string;
  movementType?: MovementType;
}

/** Bir günün lot-bazlı hareket detayı (genişletilince çekilir). */
function DayDetail({ day, filters, isMobile = false }: { day: string; filters: Filters; isMobile?: boolean }) {
  const detailQ = trpc.inventory.movements.useQuery(
    {
      page: 1, pageSize: 200,
      dateFrom: day, dateTo: day,
      itemKind: filters.itemKind,
      itemId: filters.itemId,
      movementType: filters.movementType,
    },
    { staleTime: 10_000 },
  );
  const rows = (detailQ.data?.rows ?? []) as Array<{
    id: string; ts: string; movementType: MovementType; itemCode: string; itemName: string;
    lotCode: string; locationCode: string; locationName: string; qty: number; referenceType: string | null; reasonCode: string | null; notes: string | null;
  }>;

  if (detailQ.isLoading) return <div style={{ padding: 12, fontSize: 12.5, color: "var(--neutral-500)" }}>Yükleniyor…</div>;
  if (rows.length === 0) return <div style={{ padding: 12, fontSize: 12.5, color: "var(--neutral-500)" }}>Bu gün için hareket yok.</div>;

  if (isMobile) {
    return (
      <div style={{ borderTop: "1px solid var(--neutral-100)" }}>
        {rows.map((m) => {
          const meta = TYPE_META[m.movementType] ?? { label: m.movementType, color: "var(--neutral-700)" };
          return (
            <div className="m-li" key={m.id} style={{ padding: "10px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: meta.color, flex: "none" }} />{meta.label}</span>
                <span className="mono" style={{ marginLeft: "auto", fontWeight: 700, color: m.qty < 0 ? "var(--danger)" : "var(--success-text)" }}>{fmtQty(m.qty)}</span>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 3 }}>{m.itemName} <span className="mono" style={{ fontSize: 10.5, color: "var(--neutral-400)", fontWeight: 500 }}>{m.itemCode}</span></div>
              <div style={{ fontSize: 11, color: "var(--neutral-500)", marginTop: 1 }}>Lot <span className="mono">{m.lotCode}</span> · {m.locationName} · <span className="mono">{fmtTime(m.ts)}</span></div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <table className="dt2" style={{ borderTop: "1px solid var(--neutral-200)" }}>
      <thead>
        <tr>
          <th style={{ width: 110 }}>Hareket</th>
          <th>Ürün</th>
          <th style={{ width: 150 }}>Lot</th>
          <th style={{ width: 140 }}>Lokasyon</th>
          <th className="num" style={{ width: 120 }}>Miktar</th>
          <th style={{ width: 130 }}>Referans</th>
          <th style={{ width: 64 }}>Saat</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((m) => {
          const meta = TYPE_META[m.movementType] ?? { label: m.movementType, color: "var(--neutral-700)" };
          return (
            <tr key={m.id}>
              <td><span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: meta.color, flex: "none" }} />{meta.label}</span></td>
              <td>{m.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{m.itemCode}</span></td>
              <td><span className="mono" style={{ fontSize: 11.5 }}>{m.lotCode}</span></td>
              <td><span style={{ fontSize: 12 }}>{m.locationName}</span><span className="sec mono" style={{ fontSize: 10.5 }}>{m.locationCode}</span></td>
              <td className="cell-num"><span className="mono" style={{ color: m.qty < 0 ? "var(--danger)" : "var(--success-text)", fontWeight: 600 }}>{fmtQty(m.qty)}</span></td>
              <td style={{ fontSize: 11 }}>
                {m.referenceType ? <span style={{ color: "var(--neutral-600)" }}>{REF_LABELS[m.referenceType] ?? m.referenceType}</span> : <span style={{ color: "var(--neutral-300)" }}>—</span>}
                {m.reasonCode ? <span className="sec" style={{ fontSize: 10 }}>{m.reasonCode}</span> : null}
              </td>
              <td><span className="mono" style={{ fontSize: 11, color: "var(--neutral-600)" }}>{fmtTime(m.ts)}</span></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Günlük stok hareketleri — gün bazında kümülatif (giriş/çıkış/net) + genişletince lot detayları. */
export function DailyMovementsSection({ isMobile = false }: { isMobile?: boolean }) {
  const [filterOpen, setFilterOpen] = useState(false);
  const today = useMemo(() => new Date(), []);
  const initFrom = useMemo(() => { const d = new Date(today); d.setDate(d.getDate() - 29); return ymd(d); }, [today]);
  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(ymd(today));
  const [kind, setKind] = useState<Kind | "">("");
  const [itemId, setItemId] = useState("");
  const [movementType, setMovementType] = useState<MovementType | "">("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filters: Filters = useMemo(() => ({
    dateFrom, dateTo,
    itemKind: kind || undefined,
    itemId: itemId || undefined,
    movementType: movementType || undefined,
  }), [dateFrom, dateTo, kind, itemId, movementType]);

  const dailyQ = trpc.inventory.dailyMovements.useQuery(filters, { staleTime: 10_000 });
  const itemOptsQ = trpc.inventory.itemOptions.useQuery(undefined, { staleTime: 60_000 });
  const itemOpts = (itemOptsQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string }>;
  const itemOptions = useMemo<SSGroup[]>(() => {
    const byKind: Record<string, SSOption[]> = { stock: [], semi: [], finished: [] };
    itemOpts.forEach((i) => byKind[i.kind]?.push({ value: i.id, label: i.name, tail: i.code }));
    const groups: SSGroup[] = [];
    for (const [k, lbl] of KIND_LABELS) if (byKind[k] && byKind[k]!.length) groups.push({ group: lbl, items: byKind[k]! });
    return groups;
  }, [itemOpts]);

  const days = (dailyQ.data?.days ?? []) as Array<{ day: string; inQty: number; outQty: number; net: number; count: number }>;
  const totalMoves = days.reduce((s, d) => s + d.count, 0);

  function toggle(day: string) {
    setExpanded((prev) => { const n = new Set(prev); if (n.has(day)) n.delete(day); else n.add(day); return n; });
  }

  if (isMobile) {
    const filterCount = [itemId, kind, movementType].filter(Boolean).length;
    return (
      <>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Başlangıç</label>
            <input className="m-input" style={{ fontFamily: "var(--font-sans)" }} type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Bitiş</label>
            <input className="m-input" style={{ fontFamily: "var(--font-sans)" }} type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <button type="button" className={`btn-md sm ${filterCount ? "primary" : "secondary"}`} onClick={() => setFilterOpen(true)}><Icon name="list2" size={14} />Filtrele{filterCount ? ` (${filterCount})` : ""}</button>
        </div>
        {dailyQ.isLoading ? <div className="m-empty">Yükleniyor…</div>
        : dailyQ.isError ? <div className="m-empty" style={{ color: "var(--danger)" }}>{dailyQ.error?.message}</div>
        : days.length === 0 ? <div className="m-empty">Bu aralıkta hareket yok — tarih/filtreyi değiştirin.</div>
        : days.map((d) => {
          const open = expanded.has(d.day);
          return (
            <MCard className="m-list" key={d.day}>
              <button type="button" onClick={() => toggle(d.day)} aria-expanded={open}
                style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%", background: "none", border: 0, padding: "12px 14px", cursor: "pointer", textAlign: "left" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                  <Icon name={open ? "chevDown" : "chevRight"} size={15} />
                  <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)" }}>{fmtDayLong(d.day)}</span>
                  <span className="badge b-info no-dot">{d.count} hareket</span>
                </span>
                <span style={{ display: "flex", gap: 16, fontSize: 11.5, paddingLeft: 23 }}>
                  <span><span style={{ color: "var(--neutral-400)" }}>Giriş </span><b className="mono" style={{ color: "var(--success-text)" }}>{fmtQtyAbs(d.inQty)}</b></span>
                  <span><span style={{ color: "var(--neutral-400)" }}>Çıkış </span><b className="mono" style={{ color: "var(--danger)" }}>{fmtQtyAbs(d.outQty)}</b></span>
                  <span><span style={{ color: "var(--neutral-400)" }}>Net </span><b className="mono" style={{ color: d.net < 0 ? "var(--danger)" : "var(--neutral-800)" }}>{fmtQty(d.net)}</b></span>
                </span>
              </button>
              {open ? <DayDetail day={d.day} filters={filters} isMobile /> : null}
            </MCard>
          );
        })}
        <div className="m-ovl" style={{ textAlign: "center", marginTop: 4 }}>{days.length} gün · {totalMoves} hareket</div>

        <Sheet open={filterOpen} title="Hareket Filtresi" onClose={() => setFilterOpen(false)}
          footer={<>
            <button type="button" className="btn-md secondary" onClick={() => { setItemId(""); setKind(""); setMovementType(""); }}>Temizle</button>
            <button type="button" className="btn-md primary" onClick={() => setFilterOpen(false)}>Uygula</button>
          </>}>
          <div className="field">
            <label className="field-label">Ürün</label>
            <SearchSelect value={itemId || null} options={itemOptions} onChange={(v) => setItemId(v ?? "")} placeholder="Tüm ürünler" searchPlaceholder="Ürün adı / kodu ara…" />
          </div>
          <MSelect label="Kart Türü" value={kind ? (KIND_LABELS.find(([k]) => k === kind)?.[1] ?? "") : ""} placeholder="Tümü"
            options={KIND_LABELS.map(([, l]) => l)} onChange={(lbl) => setKind((KIND_LABELS.find(([, l]) => l === lbl)?.[0] ?? "") as Kind | "")} />
          <MSelect label="Hareket Türü" value={movementType ? (TYPE_META[movementType]?.label ?? "") : ""} placeholder="Tümü"
            options={TYPE_OPTS.map(([, m]) => m.label)} onChange={(lbl) => setMovementType((TYPE_OPTS.find(([, m]) => m.label === lbl)?.[0] ?? "") as MovementType | "")} />
        </Sheet>
      </>
    );
  }

  return (
    <div className="dt2-wrap">
      <div style={{ display: "flex", alignItems: "flex-end", gap: 14, padding: "12px 14px", borderBottom: "1px solid var(--neutral-200)", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 150px", minWidth: 140 }}>
          <span style={fLabel}>Başlangıç</span>
          <input className="form-input" type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} style={{ height: 36, width: "100%" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 150px", minWidth: 140 }}>
          <span style={fLabel}>Bitiş</span>
          <input className="form-input" type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} style={{ height: 36, width: "100%" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1.6 1 210px", minWidth: 190 }}>
          <span style={fLabel}>Ürün</span>
          <SearchSelect value={itemId || null} options={itemOptions} onChange={(v) => setItemId(v ?? "")} placeholder="Tüm ürünler" searchPlaceholder="Ürün adı / kodu ara…" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 150px", minWidth: 140 }}>
          <span style={fLabel}>Kart Türü</span>
          <select className="form-select" value={kind} onChange={(e) => setKind(e.target.value as Kind | "")} style={{ height: 36, width: "100%" }}>
            <option value="">Tümü</option>
            {KIND_LABELS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 160px", minWidth: 150 }}>
          <span style={fLabel}>Hareket Türü</span>
          <select className="form-select" value={movementType} onChange={(e) => setMovementType(e.target.value as MovementType | "")} style={{ height: 36, width: "100%" }}>
            <option value="">Tümü</option>
            {TYPE_OPTS.map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
          </select>
        </div>
        <button type="button" className="btn" style={{ height: 36, flexShrink: 0 }} onClick={() => dailyQ.refetch()} disabled={dailyQ.isFetching}>
          {dailyQ.isFetching ? "Yükleniyor…" : "Yenile"}
        </button>
      </div>

      <div className="dt2-scroll">
        {dailyQ.isLoading ? (
          <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
        ) : dailyQ.isError ? (
          <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{dailyQ.error?.message}</p></div>
        ) : days.length === 0 ? (
          <div className="empty-state"><p className="ttl">Bu aralıkta hareket yok</p><p className="sub">Tarih aralığını veya filtreleri değiştirin.</p></div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 10 }}>
            {days.map((d) => {
              const open = expanded.has(d.day);
              return (
                <div key={d.day} className="d-card" style={{ padding: 0, overflow: "hidden" }}>
                  <button type="button" onClick={() => toggle(d.day)} aria-expanded={open}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", background: "transparent", border: 0, cursor: "pointer", textAlign: "left" }}>
                    <Icon name={open ? "chevDown" : "chevRight"} size={16} />
                    <span className="cell-id" style={{ minWidth: 220 }}>{fmtDayLong(d.day)}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ display: "flex", gap: 16, fontSize: 11.5, alignItems: "baseline" }}>
                      <span><span style={{ color: "var(--neutral-400)" }}>Giriş </span><b className="mono" style={{ color: "var(--success-text)" }}>{fmtQtyAbs(d.inQty)}</b></span>
                      <span><span style={{ color: "var(--neutral-400)" }}>Çıkış </span><b className="mono" style={{ color: "var(--danger)" }}>{fmtQtyAbs(d.outQty)}</b></span>
                      <span><span style={{ color: "var(--neutral-400)" }}>Net </span><b className="mono" style={{ color: d.net < 0 ? "var(--danger)" : "var(--neutral-800)" }}>{fmtQty(d.net)}</b></span>
                    </span>
                    <span className="badge-pill b-info">{d.count} hareket</span>
                  </button>
                  {open ? <DayDetail day={d.day} filters={filters} /> : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="dt2-foot">
        <div><b>{days.length}</b> gün · <b>{totalMoves}</b> hareket</div>
      </div>
    </div>
  );
}
